interface CancellableMouseOverEvent {
    mouseOverDelay?: number;
    mouseOver?: (event: MouseEvent, data?: any) => void;
    mouseOut?: (event: MouseEvent, data?: any) => void;
    data?: any;
}

const DEFAULT_MOUSE_OVER_DELAY = 800;

/**
 * Provides a class to manage mouseover and mouseout events on an HTML element with the ability
 * to introduce a delay for triggering the mouseover callback and to cancel the callback if a
 * mouseout event occurs before the delay expires.
 */
export class CancellableMouseOverWithDelay {
    private readonly mouseOverDelay: number;
    private readonly mouseOver?: (event: MouseEvent, data?: any) => void;
    private readonly mouseOut?: (event: MouseEvent, data?: any) => void;

    private readonly onMouseOverListener: (event: MouseEvent) => void;
    private readonly onMouseOutListener: (event: MouseEvent) => void;

    private mouseOverEvent: MouseEvent | undefined;
    private mouseOverEventTime: number | undefined;
    private mouseOverListenerCalled: boolean = false;

    public data?: any;
    private heartBeatInterval: ReturnType<typeof setInterval> | undefined = undefined;

    constructor(public element: HTMLElement, options?: CancellableMouseOverEvent) {
        this.mouseOver = options?.mouseOver;
        this.mouseOut = options?.mouseOut;
        this.data = options?.data;
        this.mouseOverDelay = options?.mouseOverDelay ?? DEFAULT_MOUSE_OVER_DELAY;
        this.onMouseOverListener = this.onMouseOver.bind(this);
        this.onMouseOutListener = this.onMouseOut.bind(this)
        this.init();
    }

    private init() {
        this.element.addEventListener('mouseover', this.onMouseOverListener, { });
        this.element.addEventListener('mouseout', this.onMouseOutListener);
        this.heartBeatInterval = setInterval(this.mouseOverDetectionHeartbeat.bind(this), this.mouseOverDelay / 4.);
    }

    private onMouseOver(event: MouseEvent) {
        if (event.target !== this.mouseOverEvent?.target) {
            this.mouseOverEventTime = Date.now();
        }
        this.mouseOverEvent = event;
    }

    private onMouseOut(event: MouseEvent) {
        this.mouseOverEvent = undefined;
        if (this.mouseOverListenerCalled) {
            this.mouseOverListenerCalled = false;
            this.mouseOut?.call(undefined, event, this.data);
        }
    }

    private mouseOverDetectionHeartbeat() {
        const now = Date.now();
        const delayMs = now - this.mouseOverEventTime!;
        if (this.mouseOverEvent && delayMs > this.mouseOverDelay) {
            this.mouseOverListenerCalled = true;
            this.mouseOver?.call(undefined, this.mouseOverEvent, this.data)
            this.mouseOverEvent = undefined;
        }
    }

    public destroy() {
        this.element.removeEventListener('mouseover', this.onMouseOverListener);
        this.element.removeEventListener('mouseout', this.onMouseOutListener);
        clearInterval(this.heartBeatInterval);
        this.heartBeatInterval = undefined;
    }
}
